import { ThemeWithSpecialty } from 'foundations-theming-specialty/utils';
import type { Theme } from 'foundations-theming-base/types';

export type PortalData = {
  portalTheme: Theme;
  portalData: BrandAndThemeData;
};

export interface BrandAndThemeContext {
  brandSettings?: BrandSettings;
  themeSettings?: ThemeSettings;
  companyName?: string | null;
  brandName?: string | null;
  membershipSessionTimeoutDurationSeconds?: number | null;
}

export interface FooterElement {
  tag: string;
  id?: string;
  attributes?: Record<string, string>;
}

export interface BrandAndThemeData {
  context?: BrandAndThemeContext;
  footerElements?: FooterElement[];
}

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends Record<string, any> ? DeepPartial<T[P]> : T[P];
};

type ThemeColorSubset<
  T extends
    keyof ThemeWithSpecialty['color'] = keyof ThemeWithSpecialty['color'],
> = Pick<ThemeWithSpecialty['color'], T>;

type ThemeBorderRadiusSubset<
  T extends
    keyof ThemeWithSpecialty['borderRadius'] = keyof ThemeWithSpecialty['borderRadius'],
> = Pick<ThemeWithSpecialty['borderRadius'], T>;

export type ThemeTypographySubset<
  T extends
    keyof ThemeWithSpecialty['typography'] = keyof ThemeWithSpecialty['typography'],
> = Pick<ThemeWithSpecialty['typography'], T>;

export interface ColorValue {
  color?: string | null;
  css?: string;
  hex?: string | null;
  opacity?: number | null;
  rgb?: string | null;
  rgba?: string | null;
}

export interface FontValue {
  font: string;
  fontSet: string;
}

export interface FontFieldValue {
  color?: string | null;
  css?: string;
  fallback?: string;
  font?: string;
  font_set?: string;
  size?: number | null;
  size_unit?: string | null;
  style?: string;
  styles?: {
    'font-family'?: string;
    'font-style'?: string;
    'font-weight'?: string;
    'text-decoration'?: string;
  };
  variant?: string;
}

export interface LogoValue {
  alt?: string;
  domain?: string | null;
  height?: number;
  link?: string;
  name?: string;
  src?: string;
  width?: number;
}

export interface FaviconValue {
  domain?: string | null;
  name?: string;
  src?: string;
}

export interface BrandSettings {
  primaryColor?: ColorValue;
  secondaryColor?: ColorValue;
  accentColor1?: ColorValue;
  accentColor2?: ColorValue;
  accentColor3?: ColorValue;
  bodyFont?: FontValue | null;
  colors?: Array<ColorValue>; // idk why the response has this and individual color keys
  fallbackFont?: FontValue | null;
  favicon?: FaviconValue | null;
  favicons?: Array<FaviconValue>;
  fonts?: Array<FontValue>;
  logo?: LogoValue | null;
  logos?: Array<LogoValue>;
  primaryFavicon?: FaviconValue | null;
  primaryFont?: FontValue | null;
  primaryLogo?: LogoValue | null;
}

export type StyleField = 'light' | 'dark';

export interface ThemeSettings {
  trellisColorFillSurfaceDefault?: StyleField;
  trellisColorFillPrimaryDefault?: ColorValue;
  trellisColorFillSecondaryDefault?: ColorValue;
  trellisColorFillTertiaryDefault?: ColorValue;
  trellisColorTextInteractiveDefault?: ColorValue;
  primaryFont?: FontFieldValue;
  bodyFont?: FontFieldValue;
  trellisBorderRadius?: 'rounded' | 'square' | 'pill';
}

export type PrimaryColorTokens = ThemeColorSubset<
  | 'fill-primary-default'
  | 'border-primary-default'
  | 'fill-primary-hover'
  | 'fill-primary-pressed'
  | 'fill-primary-disabled'
  | 'text-primary-default'
  | 'text-primary-disabled'
  | 'icon-primary-default'
>;

export type SecondaryColorTokens = ThemeColorSubset<
  | 'fill-secondary-default'
  | 'fill-secondary-default-alt'
  | 'fill-secondary-hover'
  | 'fill-secondary-hover-alt'
  | 'fill-secondary-pressed'
  | 'fill-secondary-pressed-alt'
  | 'fill-secondary-disabled'
  | 'fill-secondary-disabled-alt'
  | 'border-secondary-default'
  | 'border-secondary-default-alt'
  | 'text-secondary-default'
  | 'text-secondary-disabled'
>;

export type TertiaryColorTokens = ThemeColorSubset<
  | 'fill-tertiary-default'
  | 'fill-tertiary-hover'
  | 'fill-tertiary-pressed'
  | 'fill-tertiary-disabled'
  | 'fill-transitional-tertiary-default'
  | 'fill-transitional-tertiary-hover'
  | 'fill-transitional-tertiary-pressed'
  | 'border-tertiary-default'
  | 'text-tertiary-default'
  | 'text-tertiary-disabled'
>;

export type FillSurfaceColorTokens = ThemeColorSubset<
  | 'fill-surface-default'
  | 'fill-surface-default-hover'
  | 'fill-surface-default-pressed'
  | 'fill-surface-raised'
  | 'fill-surface-recessed'
  | 'fill-surface-recessed-hover'
  | 'fill-surface-recessed-pressed'
  | 'border-core-default'
  | 'border-core-subtle'
  | 'border-core-disabled'
  | 'border-core-hover'
  | 'border-transitional-core-subtle'
  | 'text-core-default'
  | 'text-core-subtle'
  | 'text-core-disabled'
  | 'text-interactive-default-disabled'
  | 'fill-field-default'
  | 'fill-field-default-alt'
  | 'fill-field-disabled'
  // tokens below affect tables
  | 'fill-field-hover'
  | 'specialty-table-header-default'
  | 'specialty-table-header-hover'
  | 'specialty-table-header-pressed'
  | 'text-core-on-fill-default'
  | 'icon-core-default'
  | 'icon-core-disabled'
  | 'icon-core-subtle'
  | 'icon-core-on-fill-default'
  | 'icon-interactive-default'
  | 'icon-interactive-disabled'
  | 'icon-interactive-hover'
  | 'icon-interactive-pressed'
  | 'icon-interactive-on-fill-default'
  | 'icon-interactive-on-fill-hover'
  | 'icon-interactive-on-fill-pressed'
  | 'icon-tertiary-default'
  // neutral fill for tooltips and similar components
  | 'fill-accent-neutral-default'
  | 'fill-accent-neutral-hover'
  | 'fill-accent-neutral-pressed'
  | 'fill-accent-neutral-subtle'
  | 'fill-accent-neutral-subtle-alt'
  | 'fill-transparent-default'
  | 'specialty-scrim'
  | 'border-transitional-core-subtle-alt'
  | 'icon-inverse-default'
  | 'text-inverse-default'
  | 'fill-transitional-modal-gradient-color-1'
  | 'fill-transitional-modal-gradient-color-2'
  | 'fill-inverse-hover-transparent'
>;

// fwc-* tokens are excluded from ThemeWithSpecialty to avoid TS2590, so they
// can't use ThemeColorSubset. This standalone type covers the fwc tokens that
// deriveDarkSurfaceOverrides needs to override for dark mode compatibility.
export type FwcDarkSurfaceTokens = Record<
  | 'fwc-button-text-core-default'
  | 'fwc-button-text-secondary-default'
  | 'fwc-button-text-tertiary-default'
  | 'fwc-link-text-core-default'
  | 'fwc-button-fill-surface-default'
  | 'fwc-button-fill-surface-raised'
  | 'fwc-button-fill-field-default-alt'
  | 'fwc-button-border-core-default'
  | 'fwc-button-border-core-hover'
  | 'fwc-radio-border-default'
  | 'fwc-radio-border-readonly'
  | 'fwc-radio-fill-readonly-indicator'
  | 'fwc-radio-text-readonly'
  | 'fwc-searchinput-border-default'
  | 'fwc-toggle-border-readonly'
  | 'fwc-progress-fill-empty'
  | 'fwc-progress-text-default'
  | 'fwc-tag-text-onfill'
  | 'fwc-tag-text-subtle'
  | 'fwc-tag-fill-default'
  | 'fwc-tag-fill-subtle'
  | 'fwc-textinput-fill-readonly'
  | 'fwc-textinput-border-readonly'
  | 'fwc-loadingspinner-ring-ghost'
  | 'fwc-loadingspinner-indicator-ghost'
  | 'fwc-loadingspinner-ring-tertiary'
  | 'fwc-loadingspinner-indicator-tertiary'
  | 'fwc-loadingspinner-ring-onfill'
  | 'fwc-loadingspinner-indicator-onfill'
  | 'fwc-loadingspinner-ring-primary'
  | 'fwc-loadingspinner-indicator-primary'
  | 'fwc-button-fill-secondary-hover-alt'
  | 'fwc-button-fill-secondary-pressed-alt'
  | 'fwc-button-text-inverse-default'
  | 'fwc-button-text-inverse-hover'
  | 'fwc-button-text-inverse-pressed'
  | 'fwc-button-text-inverse-disabled'
  | 'fwc-button-border-inverse-default'
  | 'fwc-button-border-inverse-disabled'
  | 'fwc-button-fill-inverse-default'
  | 'fwc-button-fill-inverse-hover'
  | 'fwc-button-fill-inverse-pressed'
  | 'fwc-button-fill-inverse-disabled',
  string
>;

// fwc-* table token(s) portals overrides in BOTH surface modes, unlike the
// dark-only corrections in FwcDarkSurfaceTokens.
export type FwcTableSurfaceTokens = Record<
  'fwc-table-divider-color-emphasis',
  string
>;

export type FwcPrimaryColorTokens = Record<
  | 'fwc-button-fill-primary-default'
  | 'fwc-button-fill-primary-hover'
  | 'fwc-button-fill-primary-pressed'
  | 'fwc-button-fill-primary-disabled'
  | 'fwc-button-border-primary-default'
  | 'fwc-button-border-primary-disabled'
  | 'fwc-button-text-primary-default'
  | 'fwc-button-text-primary-disabled',
  string
>;

export type FwcSecondaryColorTokens = Record<
  | 'fwc-button-fill-secondary-default'
  | 'fwc-button-fill-secondary-default-alt'
  | 'fwc-button-fill-secondary-hover'
  | 'fwc-button-fill-secondary-hover-alt'
  | 'fwc-button-fill-secondary-pressed'
  | 'fwc-button-fill-secondary-pressed-alt'
  | 'fwc-button-fill-secondary-disabled'
  | 'fwc-button-fill-secondary-disabled-alt'
  | 'fwc-button-border-secondary-default'
  | 'fwc-button-border-secondary-default-alt'
  | 'fwc-button-border-secondary-disabled'
  | 'fwc-button-border-secondary-disabled-alt'
  | 'fwc-button-text-secondary-default'
  | 'fwc-button-text-secondary-disabled',
  string
>;

export type FwcTertiaryColorTokens = Record<
  | 'fwc-button-fill-tertiary-default'
  | 'fwc-button-fill-tertiary-hover'
  | 'fwc-button-fill-tertiary-pressed'
  | 'fwc-button-fill-tertiary-disabled'
  | 'fwc-button-border-tertiary-default'
  | 'fwc-button-border-tertiary-disabled'
  | 'fwc-button-text-tertiary-default'
  | 'fwc-button-text-tertiary-disabled',
  string
>;

export type FwcTextInteractiveColorTokens = Record<
  | 'fwc-link-specialty-text-interactive-alt-default'
  | 'fwc-link-specialty-text-interactive-alt-hover'
  | 'fwc-link-specialty-text-interactive-alt-pressed'
  | 'fwc-link-specialty-text-core-alt-default'
  | 'fwc-link-text-interactive-default'
  | 'fwc-link-text-interactive-default-hover'
  | 'fwc-link-text-interactive-default-pressed'
  | 'fwc-button-specialty-text-interactive-alt-default'
  | 'fwc-button-specialty-text-interactive-alt-hover'
  | 'fwc-button-specialty-text-interactive-alt-pressed'
  | 'fwc-button-specialty-text-core-alt-default'
  | 'fwc-link-underline-default'
  | 'fwc-link-underline-hover'
  | 'fwc-link-underline-pressed'
  | 'fwc-link-underline-disabled'
  | 'fwc-link-text-alert-default'
  | 'fwc-loadingspinner-ring-link'
  | 'fwc-loadingspinner-indicator-link',
  string
>;

export type FwcAccentRedColorTokens = Record<
  | 'fwc-button-fill-alert-default'
  | 'fwc-button-fill-alert-hover'
  | 'fwc-button-fill-alert-pressed'
  | 'fwc-button-text-alert-on-fill-default'
  | 'fwc-badge-fill-candy-apple'
  | 'fwc-badge-border-candy-apple'
  | 'fwc-ribbon-fill-candy-apple'
  | 'fwc-tag-fill-candy-apple'
  | 'fwc-tag-border-candy-apple'
  | 'fwc-tag-fill-badge-candy-apple'
  | 'fwc-tag-border-badge-candy-apple'
  | 'fwc-badge-fill-red'
  | 'fwc-badge-border-red'
  | 'fwc-tag-fill-red'
  | 'fwc-tag-border-red'
  | 'fwc-tag-fill-badge-red'
  | 'fwc-tag-border-badge-red'
  | 'fwc-loadingspinner-indicator-danger'
  | 'fwc-loadingspinner-ring-danger',
  string
>;

export type FwcAccentGreenColorTokens = Record<
  | 'fwc-badge-fill-oz'
  | 'fwc-badge-border-oz'
  | 'fwc-badge-fill-new'
  | 'fwc-badge-border-new'
  | 'fwc-ribbon-fill-oz'
  | 'fwc-ribbon-fill-new'
  | 'fwc-tag-fill-oz'
  | 'fwc-tag-border-oz'
  | 'fwc-tag-fill-badge-oz'
  | 'fwc-tag-border-badge-oz'
  | 'fwc-tag-fill-green'
  | 'fwc-tag-border-green'
  | 'fwc-tag-fill-badge-green'
  | 'fwc-tag-border-badge-green'
  | 'fwc-loadingspinner-indicator-success'
  | 'fwc-loadingspinner-ring-success',
  string
>;

export type FwcCautionColorTokens = Record<
  | 'fwc-badge-fill-marigold'
  | 'fwc-badge-border-marigold'
  | 'fwc-ribbon-fill-marigold'
  | 'fwc-tag-fill-marigold'
  | 'fwc-tag-border-marigold'
  | 'fwc-tag-fill-badge-marigold'
  | 'fwc-tag-border-badge-marigold'
  | 'fwc-tag-fill-yellow'
  | 'fwc-tag-border-yellow'
  | 'fwc-tag-fill-badge-yellow'
  | 'fwc-tag-border-badge-yellow',
  string
>;

export type FwcInfoColorTokens = Record<
  | 'fwc-alert-border-default'
  | 'fwc-badge-fill-calypso'
  | 'fwc-badge-border-calypso'
  | 'fwc-ribbon-fill-calypso'
  | 'fwc-tag-fill-badge-calypso'
  | 'fwc-tag-border-badge-calypso'
  | 'fwc-tag-fill-blue'
  | 'fwc-tag-border-blue'
  | 'fwc-tag-fill-badge-blue'
  | 'fwc-tag-border-badge-blue',
  string
>;

export type FwcAccentOrangeColorTokens = Record<
  | 'fwc-badge-fill-lorax'
  | 'fwc-badge-border-lorax'
  | 'fwc-badge-fill-free'
  | 'fwc-badge-border-free'
  | 'fwc-badge-fill-sorbet'
  | 'fwc-badge-border-sorbet'
  | 'fwc-ribbon-fill-lorax'
  | 'fwc-ribbon-fill-free'
  | 'fwc-ribbon-fill-sorbet'
  | 'fwc-tag-fill-lorax'
  | 'fwc-tag-border-lorax'
  | 'fwc-tag-fill-badge-lorax'
  | 'fwc-tag-border-badge-lorax'
  | 'fwc-tag-fill-sorbet'
  | 'fwc-tag-border-sorbet'
  | 'fwc-tag-fill-badge-sorbet'
  | 'fwc-tag-border-badge-sorbet'
  | 'fwc-tag-fill-orange'
  | 'fwc-tag-border-orange'
  | 'fwc-tag-fill-badge-orange'
  | 'fwc-tag-border-badge-orange',
  string
>;

export type FwcAccentPurpleColorTokens = Record<
  | 'fwc-badge-fill-thunderdome'
  | 'fwc-badge-border-thunderdome'
  | 'fwc-badge-fill-beta'
  | 'fwc-badge-border-beta'
  | 'fwc-ribbon-fill-thunderdome'
  | 'fwc-ribbon-fill-beta'
  | 'fwc-tag-fill-thunderdome'
  | 'fwc-tag-border-thunderdome'
  | 'fwc-tag-fill-badge-thunderdome'
  | 'fwc-tag-border-badge-thunderdome'
  | 'fwc-tag-fill-purple'
  | 'fwc-tag-border-purple'
  | 'fwc-tag-fill-badge-purple'
  | 'fwc-tag-border-badge-purple',
  string
>;

export type FwcBorderRadiusTokens = Record<
  | 'fwc-button'
  | 'fwc-progress'
  | 'fwc-alert'
  | 'fwc-tag'
  | 'fwc-tag-badge'
  | 'fwc-select-options-container'
  | 'fwc-dropdown-options-container'
  | 'fwc-expandabletext-scrollable-container',
  string
>;

export type TextInteractiveColorTokens = ThemeColorSubset<
  | 'text-interactive-default'
  | 'text-interactive-default-hover'
  | 'text-interactive-default-pressed'
  | 'specialty-text-interactive-alt-default'
  | 'specialty-text-interactive-alt-hover'
  | 'specialty-text-interactive-alt-pressed'
  | 'border-interactive-default'
  | 'specialty-text-core-alt-default'
>;

export type BorderRadiusTokens = ThemeBorderRadiusSubset<
  | '0'
  | '100'
  | '200'
  | '300'
  | '400'
  | 'full'
  | 'transitional-container'
  | 'transitional-component'
  | 'transitional-container-alt'
  | 'transitional-floating'
  | 'transitional-full-0'
  | 'transitional-full-3'
  | 'transitional-full-8'
>;

export type CautionColorTokens = ThemeColorSubset<
  | 'fill-caution-default'
  | 'fill-caution-hover'
  | 'fill-caution-pressed'
  | 'fill-caution-subtle'
  | 'fill-accent-yellow-subtle-alt'
  | 'border-caution-default'
>;

export type AccentGreenColorTokens = ThemeColorSubset<
  | 'fill-accent-green-default'
  | 'fill-accent-green-subtle'
  | 'fill-accent-green-subtle-alt'
  | 'fill-accent-green-pressed'
  | 'fill-accent-green-hover'
  | 'border-accent-green-default'
  | 'fill-positive-subtle'
  | 'border-positive-default'
>;

export type AccentRedColorTokens = ThemeColorSubset<
  | 'fill-accent-red-default'
  | 'fill-accent-red-subtle'
  | 'fill-accent-red-subtle-alt'
  | 'fill-accent-red-pressed'
  | 'fill-accent-red-hover'
  | 'fill-alert-subtle'
  | 'fill-transitional-alert'
  | 'border-alert-default'
  | 'text-alert-default'
  | 'text-alert-on-fill-default'
>;

export type InfoColorTokens = ThemeColorSubset<
  | 'fill-info-default'
  | 'fill-info-subtle'
  | 'fill-info-hover'
  | 'fill-info-pressed'
  | 'border-info-default'
  | 'border-transitional-interactive-subtle'
  | 'fill-accent-blue-default'
  | 'fill-accent-blue-subtle'
>;

// these tokens are the 1:1 connection to the brand and theme settings
// from these tokens we will derive the variants necessary for full theming
export type ProtectedTopLevelColorTokens = keyof ThemeColorSubset<
  | 'fill-primary-default'
  | 'fill-secondary-default'
  | 'fill-tertiary-default'
  | 'text-interactive-default'
  | 'fill-caution-default'
  | 'fill-accent-green-default'
  | 'fill-accent-red-default'
  | 'fill-info-default'
  | 'fill-accent-orange-default'
  | 'fill-accent-purple-default'
>;

export type ProtectedColorTokens =
  | keyof PrimaryColorTokens
  | keyof SecondaryColorTokens
  | keyof TertiaryColorTokens
  | keyof FillSurfaceColorTokens
  | keyof TextInteractiveColorTokens
  | keyof CautionColorTokens
  | keyof AccentGreenColorTokens
  | keyof AccentRedColorTokens
  | keyof InfoColorTokens;

export type ProtectedBorderRadiusTokens = keyof BorderRadiusTokens;

// Not for consumer use
export type BodyTypographyTokens = keyof ThemeTypographySubset<
  | 'body-100'
  | 'body-125'
  | 'body-200'
  | 'body-300'
  | 'body-400'
  | 'body-500'
  | 'body-600'
  | 'body-700'
  | 'detail-100'
  | 'detail-200'
  | 'link-25'
  | 'link-100'
  | 'link-200'
>;

// Not for consumer use
export type HeadingTypographyTokens = keyof ThemeTypographySubset<
  | 'heading-25'
  | 'heading-50'
  | 'heading-100'
  | 'heading-200'
  | 'heading-300'
  | 'heading-400'
  | 'heading-500'
  | 'heading-600'
  | 'heading-700'
  | 'heading-800'
  | 'heading-900'
  | 'heading-1000'
  | 'display-100'
  | 'display-200'
  | 'display-300'
  | 'display-400'
  | 'display-500'
>;

export type ProtectedTypographyTokens =
  | BodyTypographyTokens
  | HeadingTypographyTokens;

// Deprecated color tokens - legacy tokens that should not be used
export type DeprecatedColorTokens = keyof ThemeColorSubset<
  | 'do-not-use-alert-danger'
  | 'do-not-use-alert-success'
  | 'do-not-use-alert-warning'
  | 'do-not-use-battleship'
  | 'do-not-use-button-danger-hover-fill'
  | 'do-not-use-button-disabled-text'
  | 'do-not-use-button-primary-hover-fill'
  | 'do-not-use-button-secondary-active-fill'
  | 'do-not-use-button-secondary-hover-fill'
  | 'do-not-use-button-secondary-text'
  | 'do-not-use-button-tertiary-light-hover-fill'
  | 'do-not-use-button-tertiary-light-text'
  | 'do-not-use-calypso'
  | 'do-not-use-calypso-dark'
  | 'do-not-use-calypso-light'
  | 'do-not-use-calypso-medium'
  | 'do-not-use-candy-apple'
  | 'do-not-use-candy-apple-dark'
  | 'do-not-use-candy-apple-light'
  | 'do-not-use-candy-apple-medium'
  | 'do-not-use-chart-aqua'
  | 'do-not-use-chart-blue'
  | 'do-not-use-chart-dark-purple'
  | 'do-not-use-chart-green'
  | 'do-not-use-chart-orange'
  | 'do-not-use-chart-purple'
  | 'do-not-use-chart-red'
  | 'do-not-use-chart-yellow'
  | 'do-not-use-cirrus'
  | 'do-not-use-eerie'
  | 'do-not-use-facebook'
  | 'do-not-use-flat-earth'
  | 'do-not-use-flint'
  | 'do-not-use-focus-ring-base'
  | 'do-not-use-google'
  | 'do-not-use-great-white'
  | 'do-not-use-gypsum'
  | 'do-not-use-heffalump'
  | 'do-not-use-instagram'
  | 'do-not-use-koala'
  | 'do-not-use-link-hover'
  | 'do-not-use-linkedin'
  | 'do-not-use-lorax'
  | 'do-not-use-lorax-dark'
  | 'do-not-use-lorax-light'
  | 'do-not-use-lorax-medium'
  | 'do-not-use-marigold'
  | 'do-not-use-marigold-dark'
  | 'do-not-use-marigold-light'
  | 'do-not-use-marigold-medium'
  | 'do-not-use-monolith'
  | 'do-not-use-norman'
  | 'do-not-use-norman-dark'
  | 'do-not-use-norman-light'
  | 'do-not-use-norman-medium'
  | 'do-not-use-obsidian'
  | 'do-not-use-olaf'
  | 'do-not-use-oz'
  | 'do-not-use-oz-dark'
  | 'do-not-use-oz-light'
  | 'do-not-use-oz-medium'
  | 'do-not-use-pantera'
  | 'do-not-use-pinterest'
  | 'do-not-use-rss'
  | 'do-not-use-slinky'
  | 'do-not-use-sorbet'
  | 'do-not-use-sorbet-dark'
  | 'do-not-use-sorbet-light'
  | 'do-not-use-sorbet-medium'
  | 'do-not-use-thunderdome'
  | 'do-not-use-thunderdome-dark'
  | 'do-not-use-thunderdome-light'
  | 'do-not-use-thunderdome-medium'
  | 'do-not-use-transparent'
  | 'do-not-use-transparent-dark'
  | 'do-not-use-transparent-light'
  | 'do-not-use-twitter'
  | 'do-not-use-x'
  | 'do-not-use-xing'
  | 'do-not-use-youtube'
  | 'temp-do-not-use-border-primary-default'
  | 'temp-do-not-use-border-primary-disabled'
  | 'temp-do-not-use-fill-accent-teal-default'
  | 'temp-do-not-use-fill-accent-teal-hover'
  | 'temp-do-not-use-fill-accent-teal-pressed'
  | 'temp-do-not-use-fill-accent-teal-subtle'
  | 'temp-do-not-use-fill-field-hover'
  | 'temp-do-not-use-fill-field-pressed'
  | 'temp-do-not-use-fill-secondary-default'
  | 'temp-do-not-use-fill-secondary-hover'
  | 'temp-do-not-use-fill-secondary-pressed'
  | 'temp-do-not-use-fill-surface-default-hover'
  | 'temp-do-not-use-fill-surface-default-pressed'
  | 'temp-do-not-use-fill-surface-recessed'
  | 'temp-do-not-use-icon-primary-default'
  | 'temp-do-not-use-icon-primary-hover'
  | 'temp-do-not-use-icon-primary-pressed'
  | 'temp-do-not-use-reporting-fill-highlight-purple-subtle'
  | 'temp-do-not-use-specialty-scrim'
  | 'temp-do-not-use-text-primary-default'
  | 'temp-do-not-use-text-primary-subtle'
  | 'temp-future-border-primary-default'
  | 'temp-future-border-primary-disabled'
  | 'temp-future-fill-accent-teal-default'
  | 'temp-future-fill-accent-teal-hover'
  | 'temp-future-fill-accent-teal-pressed'
  | 'temp-future-fill-accent-teal-subtle'
  | 'temp-future-fill-field-hover'
  | 'temp-future-fill-field-pressed'
  | 'temp-future-fill-secondary-default'
  | 'temp-future-fill-secondary-hover'
  | 'temp-future-fill-secondary-pressed'
  | 'temp-future-fill-surface-default-hover'
  | 'temp-future-fill-surface-default-pressed'
  | 'temp-future-fill-surface-recessed'
  | 'temp-future-icon-primary-default'
  | 'temp-future-icon-primary-hover'
  | 'temp-future-icon-primary-pressed'
  | 'temp-future-reporting-fill-highlight-purple-subtle'
  | 'temp-future-specialty-scrim'
  | 'temp-future-text-primary-default'
  | 'temp-future-text-primary-subtle'
  | 'text-accent-blue-default'
  | 'text-accent-green-default'
  | 'text-accent-magenta-default'
  | 'text-accent-purple-default'
  | 'text-accent-red-default'
  | 'text-accent-teal-default'
  | 'text-accent-yellow-default'
>;

// System color tokens - internal system tokens that should not be overridden by consumers
export type SystemColorTokens = keyof ThemeColorSubset<
  | 'breeze-border-badge-default'
  | 'breeze-border-field-default'
  | 'breeze-border-secondary-default'
  | 'breeze-border-secondary-disabled'
  | 'breeze-border-secondary-hover'
  | 'breeze-border-secondary-pressed'
  | 'breeze-border-tertiary-default'
  | 'breeze-border-tertiary-disabled'
  | 'breeze-border-tertiary-hover'
  | 'breeze-border-tertiary-pressed'
  | 'breeze-fill-badge-default'
  | 'breeze-fill-component-disabled'
  | 'breeze-fill-feedback-active'
  | 'breeze-fill-feedback-default'
  | 'breeze-fill-feedback-hover'
  | 'breeze-fill-loading-dot-default'
  | 'breeze-fill-primary-default'
  | 'breeze-fill-primary-disabled'
  | 'breeze-fill-primary-hover'
  | 'breeze-fill-primary-pressed'
  | 'breeze-fill-secondary-default'
  | 'breeze-fill-secondary-disabled'
  | 'breeze-fill-secondary-hover'
  | 'breeze-fill-secondary-pressed'
  | 'breeze-fill-shimmer-default'
  | 'breeze-fill-special-btn-alt-default'
  | 'breeze-fill-special-btn-alt-disabled'
  | 'breeze-fill-special-btn-default'
  | 'breeze-fill-special-btn-hover'
  | 'breeze-fill-special-btn-pressed'
  | 'breeze-fill-tertiary-default'
  | 'breeze-fill-tertiary-disabled'
  | 'breeze-fill-tertiary-hover'
  | 'breeze-fill-tertiary-pressed'
  | 'breeze-fill-transparent-defauilt'
  | 'breeze-icon-component-disabled'
  | 'breeze-icon-feedback-active'
  | 'breeze-icon-feedback-default'
  | 'breeze-icon-field-default'
  | 'breeze-icon-field-disabled'
  | 'breeze-icon-full-page-loader-default'
  | 'breeze-icon-icon-button-default'
  | 'breeze-icon-icon-button-hover'
  | 'breeze-icon-icon-button-pressed'
  | 'breeze-icon-primary-default'
  | 'breeze-icon-primary-disabled'
  | 'breeze-icon-secondary-default'
  | 'breeze-icon-special-btn-default'
  | 'breeze-icon-tertiary-default'
  | 'breeze-icon-transparent-default'
  | 'breeze-text-badge-default'
  | 'breeze-text-component-disabled'
  | 'breeze-text-field-default'
  | 'breeze-text-field-disabled'
  | 'breeze-text-field-input'
  | 'breeze-text-primary-default'
  | 'breeze-text-primary-disabled'
  | 'breeze-text-secondary-default'
  | 'breeze-text-secondary-disabled'
  | 'breeze-text-secondary-hover'
  | 'breeze-text-secondary-pressed'
  | 'breeze-text-special-btn-default'
  | 'breeze-text-tertiary-default'
  | 'breeze-text-tertiary-disabled'
  | 'breeze-text-transparent-default'
  | 'breeze-text-transparent-hover'
  | 'breeze-text-transparent-pressed'
  | 'global-shell-border-dark'
  | 'global-shell-border-default'
  | 'global-shell-border-search-btn-border-default'
  | 'global-shell-border-search-btn-border-hover'
  | 'global-shell-border-search-field-border-default'
  | 'global-shell-border-subtle'
  | 'global-shell-fill-editortitle-hover'
  | 'global-shell-fill-logo-default'
  | 'global-shell-fill-nav-item-default'
  | 'global-shell-fill-nav-item-hover'
  | 'global-shell-fill-nav-item-selected'
  | 'global-shell-fill-search-default'
  | 'global-shell-fill-search-hover'
  | 'global-shell-fill-sub-nav-item-default'
  | 'global-shell-fill-sub-nav-item-hover'
  | 'global-shell-fill-sub-nav-item-selected'
  | 'global-shell-fill-surface-default'
  | 'global-shell-fill-surface-panel-default'
  | 'global-shell-icon-bookmark-default'
  | 'global-shell-icon-bookmark-hover'
  | 'global-shell-icon-bookmark-selected'
  | 'global-shell-icon-copilot-default'
  | 'global-shell-icon-nav-item-default'
  | 'global-shell-icon-nav-item-hover'
  | 'global-shell-icon-nav-item-selected'
  | 'global-shell-icon-nav-item-subtle'
  | 'global-shell-text-copilot-default'
  | 'global-shell-text-copilot-hover'
  | 'global-shell-text-nav-item-default'
  | 'global-shell-text-nav-item-hover'
  | 'global-shell-text-nav-item-selected'
>;

type FillAccentColorTokens = keyof ThemeColorSubset<
  | 'fill-accent-light-orange-default'
  | 'fill-accent-magenta-default'
  | 'fill-accent-neutral-default'
  | 'fill-accent-orange-default'
  | 'fill-accent-purple-default'
  | 'fill-accent-teal-default'
  | 'fill-accent-yellow-default'
>;

type FillSemanticColorTokens = keyof ThemeColorSubset<
  | 'fill-alert-default'
  | 'fill-brand-default'
  | 'fill-inverse-default'
  | 'fill-positive-default'
  | 'fill-surface-default'
  | 'fill-transparent-default'
>;

type TextSemanticColorTokens = keyof ThemeColorSubset<
  'text-alert-default' | 'text-core-default' | 'text-positive-default'
>;

type IconAccentColorTokens = keyof ThemeColorSubset<
  | 'icon-accent-blue-default'
  | 'icon-accent-green-default'
  | 'icon-accent-magenta-default'
  | 'icon-accent-orange-default'
  | 'icon-accent-purple-default'
  | 'icon-accent-red-default'
  | 'icon-accent-teal-default'
  | 'icon-accent-yellow-default'
>;

type IconSemanticColorTokens = keyof ThemeColorSubset<
  | 'icon-alert-default'
  | 'icon-core-default'
  | 'icon-interactive-default'
  | 'icon-inverse-default-alt'
  | 'icon-positive-default'
>;

type BorderAccentColorTokens = keyof ThemeColorSubset<
  | 'border-accent-blue-default'
  | 'border-accent-green-default'
  | 'border-accent-light-orange-default'
  | 'border-accent-magenta-default'
  | 'border-accent-orange-default'
  | 'border-accent-purple-default'
  | 'border-accent-red-default'
  | 'border-accent-yellow-default'
>;

type BorderSemanticColorTokens = keyof ThemeColorSubset<
  | 'border-alert-default'
  | 'border-container-default'
  | 'border-core-default'
  | 'border-inverse-default'
  | 'border-positive-default'
  | 'border-surface-default'
>;

// All available base color tokens
type AvailableColorTokens =
  | FillAccentColorTokens
  | FillSemanticColorTokens
  | TextSemanticColorTokens
  | IconAccentColorTokens
  | IconSemanticColorTokens
  | BorderAccentColorTokens
  | BorderSemanticColorTokens;

type ChartStatusColorTokens = keyof ThemeColorSubset<
  | 'reporting-fill-chart-alert'
  | 'reporting-fill-chart-caution'
  | 'reporting-fill-chart-positive'
  | 'reporting-fill-chart-white'
>;

type ChartDefaultDarkColorTokens = keyof ThemeColorSubset<
  | 'reporting-fill-chart-default-dark-aqua'
  | 'reporting-fill-chart-default-dark-blue'
  | 'reporting-fill-chart-default-dark-green'
  | 'reporting-fill-chart-default-dark-orange'
  | 'reporting-fill-chart-default-dark-pink'
  | 'reporting-fill-chart-default-dark-purple'
  | 'reporting-fill-chart-default-dark-yellow'
>;

type ChartDefaultLightColorTokens = keyof ThemeColorSubset<
  | 'reporting-fill-chart-default-light-aqua'
  | 'reporting-fill-chart-default-light-blue'
  | 'reporting-fill-chart-default-light-green'
  | 'reporting-fill-chart-default-light-orange'
  | 'reporting-fill-chart-default-light-pink'
  | 'reporting-fill-chart-default-light-purple'
  | 'reporting-fill-chart-default-light-yellow'
>;

type ChartScaleDarkColorTokens = keyof ThemeColorSubset<
  | 'reporting-fill-chart-scale-dark-aqua'
  | 'reporting-fill-chart-scale-dark-blue'
  | 'reporting-fill-chart-scale-dark-green'
  | 'reporting-fill-chart-scale-dark-orange'
  | 'reporting-fill-chart-scale-dark-pink'
  | 'reporting-fill-chart-scale-dark-purple'
  | 'reporting-fill-chart-scale-dark-yellow'
>;

type ChartScaleLightColorTokens = keyof ThemeColorSubset<
  | 'reporting-fill-chart-scale-light-aqua'
  | 'reporting-fill-chart-scale-light-blue'
  | 'reporting-fill-chart-scale-light-green'
  | 'reporting-fill-chart-scale-light-orange'
  | 'reporting-fill-chart-scale-light-pink'
  | 'reporting-fill-chart-scale-light-purple'
  | 'reporting-fill-chart-scale-light-yellow'
>;

type ReportingHighlightColorTokens = keyof ThemeColorSubset<
  | 'reporting-fill-highlight-blue-default'
  | 'reporting-fill-highlight-blue-subtle'
  | 'reporting-fill-highlight-green-default'
  | 'reporting-fill-highlight-green-subtle'
  | 'reporting-fill-highlight-orange-default'
  | 'reporting-fill-highlight-orange-subtle'
  | 'reporting-fill-highlight-purple-default'
  | 'reporting-fill-highlight-purple-subtle'
  | 'reporting-fill-highlight-red-default'
  | 'reporting-fill-highlight-red-subtle'
  | 'reporting-fill-highlight-yellow-default'
  | 'reporting-fill-highlight-yellow-subtle'
>;

// All available reporting color tokens
type AvailableReportingColorTokens =
  | ChartStatusColorTokens
  | ChartDefaultDarkColorTokens
  | ChartDefaultLightColorTokens
  | ChartScaleDarkColorTokens
  | ChartScaleLightColorTokens
  | ReportingHighlightColorTokens;

export type AvailableTokens =
  | AvailableColorTokens
  | AvailableReportingColorTokens;

// These arrays define which variants each token category supports
// Only AvailableTokens prefixes are permitted
export const NO_VARIANTS = [] as const;
export const SINGLE_VARIANT = ['default'] as const;
export const SUBTLE_VARIANT = ['default', 'subtle'] as const;
export const HOVER_PRESSED_VARIANT = ['default', 'hover', 'pressed'] as const;
export const COMPLETE_VARIANT = [
  'default',
  'hover',
  'pressed',
  'subtle',
] as const;

export type VariantSet =
  | typeof NO_VARIANTS
  | typeof SINGLE_VARIANT
  | typeof SUBTLE_VARIANT
  | typeof HOVER_PRESSED_VARIANT
  | typeof COMPLETE_VARIANT;

// Extracts the prefix from token names (e.g., 'fill-brand-default' → 'fill-brand').
// Only handles -default and -subtle because those are the only suffixes in AvailableTokens.
// Variants like -hover and -pressed are derived automatically and not directly targetable.
// We can't rely solely on default because there are some reporting tokens who only have a subtle variant
// without a companion default variant.
type ExtractTokenPrefix<T extends string> = T extends `${infer Prefix}-default`
  ? Prefix
  : T extends `${infer Prefix}-subtle`
  ? Prefix
  : T;

export type AvailableTokenPrefixes = ExtractTokenPrefix<AvailableTokens>;

// Defines which variants each token prefix supports and optional derived tokens.
export interface TokenVariantConfig {
  prefixes: readonly AvailableTokenPrefixes[];
  variants: VariantSet;
  derivedTokens?: {
    text?: string;
    border?: string;
    icon?: string;
  };
}

export const TOKEN_VARIANT_CONFIGS: TokenVariantConfig[] = [
  {
    prefixes: [
      'reporting-fill-chart-alert',
      'reporting-fill-chart-caution',
      'reporting-fill-chart-positive',
      'reporting-fill-chart-white',
    ],
    variants: NO_VARIANTS,
  },
  {
    prefixes: [
      'icon-accent-blue',
      'icon-accent-green',
      'icon-accent-magenta',
      'icon-accent-orange',
      'icon-accent-purple',
      'icon-accent-red',
      'icon-accent-teal',
      'icon-accent-yellow',
    ],
    variants: SINGLE_VARIANT,
  },
  {
    prefixes: [
      'border-accent-blue',
      'border-accent-light-orange',
      'border-accent-magenta',
      'border-accent-orange',
      'border-accent-purple',
      'border-accent-red',
      'border-accent-yellow',
    ],
    variants: SINGLE_VARIANT,
  },
  {
    prefixes: ['border-container'],
    variants: SINGLE_VARIANT,
  },
  {
    prefixes: [
      'reporting-fill-highlight-blue',
      'reporting-fill-highlight-green',
      'reporting-fill-highlight-orange',
      'reporting-fill-highlight-purple',
      'reporting-fill-highlight-red',
      'reporting-fill-highlight-yellow',
    ],
    variants: SUBTLE_VARIANT,
  },
  {
    prefixes: ['icon-alert'],
    variants: HOVER_PRESSED_VARIANT,
  },
  {
    prefixes: [
      'fill-accent-light-orange',
      'fill-accent-magenta',
      'fill-accent-orange',
      'fill-accent-purple',
      'fill-accent-teal',
    ],
    variants: COMPLETE_VARIANT,
  },
  {
    prefixes: ['fill-brand'],
    variants: COMPLETE_VARIANT,
    derivedTokens: {
      text: 'text-brand',
      border: 'border-brand',
      icon: 'icon-brand',
    },
  },
];

export type TokenOverrides = {
  [cmsThemeField: string]: AvailableTokens;
};

export type ProtectedColorThemeSettingFields = keyof Pick<
  ThemeSettings,
  | 'trellisColorFillPrimaryDefault'
  | 'trellisColorFillSecondaryDefault'
  | 'trellisColorFillTertiaryDefault'
  | 'trellisColorTextInteractiveDefault'
>;

export type ProtectedFontThemeSettingFields = keyof Pick<
  ThemeSettings,
  'primaryFont' | 'bodyFont'
>;

export type BrandFields =
  | keyof Pick<
      BrandSettings,
      'primaryColor' | 'secondaryColor' | 'accentColor1'
    >
  | null;

export type BorderRadiusThemeSettingFields = keyof Pick<
  ThemeSettings,
  'trellisBorderRadius'
>;

export type FontFields = keyof Pick<BrandSettings, 'bodyFont' | 'primaryFont'>;

export interface PortalInstance {
  accessGroupIds: number[];
  id: number;
  portalId: number;
  domain: string;
  slug: string;
  isEnabled: boolean;
  internalName: string;
  displayName: string;
  createdAt: number;
  updatedAt: number;
  deletedAt: number;
  portalInstanceType: 'CUSTOMER' | 'BILLING' | 'BUYER' | 'REFERENCE';
  themePath: string;
}

// Currently these constants and types are only used in the reference app's token explorer
// in order to keep the token explorer up to date with the latest tokens we are using within
// the theming system.
export type PortalsSurfaceTokenName = Extract<
  keyof FillSurfaceColorTokens,
  string
>;
export type PortalsPrimaryColorTokenName = Extract<
  keyof PrimaryColorTokens,
  string
>;
export type PortalsSecondaryColorTokenName = Extract<
  keyof SecondaryColorTokens,
  string
>;
export type PortalsAccentColorOneTokenName = Extract<
  keyof TertiaryColorTokens,
  string
>;
export type PortalsHeadingFontTokenName = Extract<
  HeadingTypographyTokens,
  string
>;
export type PortalsBodyFontTokenName = Extract<BodyTypographyTokens, string>;

export const PORTALS_SURFACE_TOKENS = {
  'border-core-default': true,
  'border-core-disabled': true,
  'border-core-hover': true,
  'border-core-subtle': true,
  'border-transitional-core-subtle': true,
  'fill-accent-neutral-default': true,
  'fill-accent-neutral-hover': true,
  'fill-accent-neutral-pressed': true,
  'fill-accent-neutral-subtle': true,
  'fill-accent-neutral-subtle-alt': true,
  'fill-field-default': true,
  'fill-field-default-alt': true,
  'fill-field-disabled': true,
  'fill-field-hover': true,
  'fill-surface-default': true,
  'fill-transparent-default': true,
  'fill-surface-default-hover': true,
  'fill-surface-default-pressed': true,
  'fill-surface-raised': true,
  'fill-surface-recessed': true,
  'fill-surface-recessed-hover': true,
  'fill-surface-recessed-pressed': true,
  'icon-core-default': true,
  'icon-core-disabled': true,
  'icon-core-on-fill-default': true,
  'icon-core-subtle': true,
  'icon-interactive-default': true,
  'icon-interactive-disabled': true,
  'icon-interactive-hover': true,
  'icon-interactive-on-fill-default': true,
  'icon-interactive-on-fill-hover': true,
  'icon-interactive-on-fill-pressed': true,
  'icon-interactive-pressed': true,
  'icon-tertiary-default': true,
  'specialty-table-header-default': true,
  'specialty-table-header-hover': true,
  'specialty-table-header-pressed': true,
  'text-core-default': true,
  'text-core-disabled': true,
  'text-core-on-fill-default': true,
  'text-core-subtle': true,
  'text-interactive-default-disabled': true,
  'specialty-scrim': true,
  'border-transitional-core-subtle-alt': true,
  'icon-inverse-default': true,
  'text-inverse-default': true,
  'fill-transitional-modal-gradient-color-1': true,
  'fill-transitional-modal-gradient-color-2': true,
  'fill-inverse-hover-transparent': true,
} as const satisfies Record<PortalsSurfaceTokenName, true>;

export const PORTALS_PRIMARY_COLOR_TOKENS = {
  'fill-primary-default': true,
  'border-primary-default': true,
  'fill-primary-hover': true,
  'fill-primary-pressed': true,
  'fill-primary-disabled': true,
  'text-primary-default': true,
  'text-primary-disabled': true,
  'icon-primary-default': true,
} as const satisfies Record<PortalsPrimaryColorTokenName, true>;

export const PORTALS_SECONDARY_COLOR_TOKENS = {
  'fill-secondary-default': true,
  'fill-secondary-default-alt': true,
  'fill-secondary-hover': true,
  'fill-secondary-hover-alt': true,
  'fill-secondary-pressed': true,
  'fill-secondary-pressed-alt': true,
  'fill-secondary-disabled': true,
  'fill-secondary-disabled-alt': true,
  'border-secondary-default': true,
  'border-secondary-default-alt': true,
  'text-secondary-default': true,
  'text-secondary-disabled': true,
} as const satisfies Record<PortalsSecondaryColorTokenName, true>;

export const PORTALS_ACCENT_COLOR_ONE_TOKENS = {
  'fill-tertiary-default': true,
  'fill-tertiary-hover': true,
  'fill-tertiary-pressed': true,
  'fill-tertiary-disabled': true,
  'fill-transitional-tertiary-default': true,
  'fill-transitional-tertiary-hover': true,
  'fill-transitional-tertiary-pressed': true,
  'border-tertiary-default': true,
  'text-tertiary-default': true,
  'text-tertiary-disabled': true,
} as const satisfies Record<PortalsAccentColorOneTokenName, true>;

export const PORTALS_HEADING_FONT_TOKENS = {
  'heading-25': true,
  'heading-50': true,
  'heading-100': true,
  'heading-200': true,
  'heading-300': true,
  'heading-400': true,
  'heading-500': true,
  'heading-600': true,
  'heading-700': true,
  'heading-800': true,
  'heading-900': true,
  'heading-1000': true,
  'display-100': true,
  'display-200': true,
  'display-300': true,
  'display-400': true,
  'display-500': true,
} as const satisfies Record<PortalsHeadingFontTokenName, true>;

export const PORTALS_BODY_FONT_TOKENS = {
  'body-100': true,
  'body-125': true,
  'body-200': true,
  'body-300': true,
  'body-400': true,
  'body-500': true,
  'body-600': true,
  'body-700': true,
  'detail-100': true,
  'detail-200': true,
  'link-25': true,
  'link-100': true,
  'link-200': true,
} as const satisfies Record<PortalsBodyFontTokenName, true>;
